import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Initiative } from '../../../../api/initiative-client/initiative-client.type';
import type { ProjectsResponse } from '../../../../api/projects-client/projects-client.type';
import type { Epic } from '../../../../api/tasks-client/task-client.type';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { useCostTabStore, useStrategyStore } from '../../../../store/strategy-store/strategy-store';
import { useCurrentStrategyTab } from '../../strategy.hooks';
import { StrategyTab } from '../../strategy.type';
import { generateEpicsTreeMapData, generateInitiativesTreeMapData, generateTeamsTreeMapData } from './cost-tab.helpers';
import { CostMode, type ChartDataItem } from './cost-tab.type';

/**
 * Hook to retrieve epics that have associated costs in the current initiative
 *
 * @returns {Epic[] | []} Array of epics with cost data or empty array if no initiative or no matching epics
 */
const useCostEpics = (): Epic[] | [] => {
  const initiative = useStrategyStore((state) => state.initiativePerformance);

  if (!initiative?.costs_to_date?.epics) {
    return [];
  }

  const { epics = [], costs_to_date } = initiative;

  const epicIds = Object.keys(costs_to_date.epics);
  const result = epics.filter((epic) => epicIds.includes(epic.id));

  return result;
};

/**
 * Hook to retrieve teams that have associated costs in the current initiative
 *
 * @returns {ProjectsResponse[] | []} Array of teams with cost data or empty array if no initiative or no matching teams
 */
const useCostTeams = (): ProjectsResponse[] | [] => {
  const globalTeams = useGlobalStore((state) => state.teams);
  const initiative = useStrategyStore((state) => state.initiativePerformance);

  if (!initiative?.costs_to_date?.teams) {
    return [];
  }

  const { costs_to_date } = initiative;

  const teamIds = Object.keys(costs_to_date.teams);
  const result = globalTeams.filter((team) => teamIds.includes(team.id));

  return result;
};

/**
 * Hook to retrieve initiatives that have associated costs in the current initiative
 *
 * @returns {Initiative[] | []} Array of initiatives with cost data or empty array if no initiative or no matching initiatives
 */
const useCostInitiatives = (): Initiative[] | [] => {
  const currentStrategyTab = useCurrentStrategyTab();
  const initiative = useStrategyStore((state) => state.initiativePerformance) as any;

  if (currentStrategyTab === StrategyTab.Initiatives) {
    return [];
  }

  const initiativeIds = Object.keys(initiative?.costs_to_date?.initiatives || {});
  return initiative?.initiatives?.filter((initiative: Initiative) => initiativeIds.includes(initiative.id)) || [];
};

/**
 * Hook to retrieve chart data for the Cost tab visualization
 *
 * @returns {ChartDataItem[] | []} Array of chart data items or empty array if no initiative or no matching data
 */
const useChartData = (): ChartDataItem[] | [] => {
  const { t } = useTranslation();
  const initiative = useStrategyStore((state) => state.initiativePerformance);
  const mode = useCostTabStore((state) => state.mode);
  const epics = useCostTabStore((state) => state.selectedEpics);
  const teams = useCostTabStore((state) => state.selectedTeams);
  const initiatives = useCostTabStore((state) => state.selectedInitiatives);

  return useMemo(() => {
    if (!initiative?.costs_to_date) {
      return [];
    }

    const { costs_to_date } = initiative as any;

    switch (mode) {
      case CostMode.ContributingEpics:
        return generateEpicsTreeMapData(costs_to_date, epics, t);
      case CostMode.ContributingTeams:
        return generateTeamsTreeMapData(costs_to_date, teams, t);
      case CostMode.ContributingInitiatives:
        return generateInitiativesTreeMapData(costs_to_date, initiatives, t);
      default:
        return [];
    }
  }, [initiative, mode, epics, teams, initiatives, t]);
};

/**
 * Hook to check if cost data has been fetched based on the current mode
 *
 * @returns {boolean} True if cost data exists for the current mode (epics or teams), false otherwise
 */
const useCostDataFetched = (): boolean => {
  const mode = useCostTabStore((state) => state.mode);
  const epics = useCostEpics();
  const teams = useCostTeams();
  const initiatives = useCostInitiatives();

  if (mode === CostMode.ContributingEpics) {
    return epics.length > 0;
  }

  if (mode === CostMode.ContributingTeams) {
    return teams.length > 0;
  }

  if (mode === CostMode.ContributingInitiatives) {
    return initiatives.length > 0;
  }

  return false;
};

/**
 * Hook to check if any filters are currently applied based on the selected mode
 *
 * @returns {boolean} True if there are selected epics in epic mode or selected teams in team mode, false otherwise
 */
const useFiltersApplied = (): boolean => {
  const selectedEpics = useCostTabStore((state) => state.selectedEpics);
  const selectedTeams = useCostTabStore((state) => state.selectedTeams);
  const selectedInitiatives = useCostTabStore((state) => state.selectedInitiatives);
  const mode = useCostTabStore((state) => state.mode);

  if (mode === CostMode.ContributingEpics) {
    return selectedEpics.length > 0;
  }

  if (mode === CostMode.ContributingTeams) {
    return selectedTeams.length > 0;
  }

  if (mode === CostMode.ContributingInitiatives) {
    return selectedInitiatives.length > 0;
  }

  return false;
};

export { useChartData, useCostDataFetched, useCostEpics, useCostInitiatives, useCostTeams, useFiltersApplied };
